import { Alignment, autoPlacement, autoUpdate, computePosition, hide, Placement, ReferenceElement, shift } from "@floating-ui/dom";
import interact from "../../../lib/interact";

type ResizableOptions = Readonly<{
  edges?: Readonly<{
    left: boolean;
    right: boolean;
    top: boolean;
  }>;
  max?: Readonly<{
    width: number;
    height: number;
  }>;
  min?: Readonly<{
    width: number;
    height: number;
  }>;
  initialMaxHeight?: string;
}>

type Anchor = Readonly<{
  x: number;
  y: number;
}> | HTMLElement;
/** */
export class UiFloatPopover {
  root: HTMLDivElement;
  handleClick: (e: MouseEvent) => void;
  hideOnOuterClick: boolean;
  onDestroy?: () => void;

  cleanUp: () => void;

  constructor(
    template: string,
    anchor: Anchor,
    allowedPlacements?: Placement[],
    hideOnOuterClick = true,
    onDestroy?: () => void,
    parent?: HTMLElement,
    resizableOptions?: ResizableOptions,
    useFlex = false,
    withShift = false,
  ) {
    this.hideOnOuterClick = hideOnOuterClick;
    this.onDestroy = onDestroy;
    this.root = document.createElement("div");
    this.root.nodeType;
    this.root.innerHTML = template;
    this.root.style.position = "absolute";
    this.root.style.zIndex = "10002000";
    this.root.style.border = "1px solid #000";
    if (this.root.nodeType) (parent ?? document.body).appendChild(this.root);

    if (resizableOptions != null) {
      if (resizableOptions.initialMaxHeight != null) {
        this.root.style.maxHeight = resizableOptions.initialMaxHeight;
      }
      this.initResizable(resizableOptions);
    }

    this.handleClick = (e) => {
      if (!this.hideOnOuterClick) {
        return;
      }

      this.removePopover.call(this, e)
    };

    const virtualElement = (anchor instanceof HTMLElement) ?
      anchor :
      {
        getBoundingClientRect() {
          return {
            x: anchor.x,
            y: anchor.y,
            width: 0,
            height: 0,
            top: anchor.y,
            left: anchor.x,
            right: anchor.x,
            bottom: anchor.y,
          };
        },
      };

    this.cleanUp = autoUpdate(virtualElement, this.root, () => this.updatePosition(virtualElement, allowedPlacements, useFlex, withShift))

    document.addEventListener("contextmenu", this.handleClick, true);
    document.addEventListener("click", this.handleClick, true);
  }

  updatePosition(anchor: ReferenceElement, allowedPlacements?: Placement[], useFlex = false, withShift = false): void {

    computePosition(anchor, this.root, {
      placement: 'right-start',
      middleware: [
        withShift ? shift() : undefined,
        autoPlacement({
          alignment: "start",
          allowedPlacements,
        }),
      ],
    }).then(({ x, y }) => {
      this.root.style.left = `${x}px`;
      this.root.style.top = `${y}px`;
      this.root.style.display = useFlex ? "flex" : "block";
    });
  }

  removePopover(e) {
    if (this.root && !this?.root?.contains(e.target)) {
      this.destroy();
    }
  }

  destroy() {
    if (this.onDestroy) {
      this.onDestroy();
    }
    document.removeEventListener("click", this.handleClick);
    document.removeEventListener("contextmenu", this.handleClick);
    if (this.root && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
    this.cleanUp();
    this.root = null;
  }

  initResizable(options: ResizableOptions): void {
    let edges = options.edges ?? { 
        left: false,
        right: false,
        bottom: true,
    }
    // @ts-expect-error
    this.interactable = interact(this.root).resizable({
      edges,
      listeners: {
        move: (event) => {
          const target = event.target
          let x = (parseFloat(target.getAttribute('data-x')) || 0)
          let y = (parseFloat(target.getAttribute('data-y')) || 0)
          target.style.width = event.rect.width + 'px'
          target.style.height = event.rect.height + 'px'
          x += event.deltaRect.left
          y += event.deltaRect.top
          target.setAttribute('data-x', x)
          target.setAttribute('data-y', y)
          this.root.style.maxHeight = '';
        }
      },
      modifiers: [
        interact.modifiers.restrictSize({
          min: options.min ?? { width: 250, height: 80 },
          max: options.max,
        }),
        interact.modifiers.restrictRect({
          restriction: 'parent',
        })
      ],
    })
  }
}
